import { type Dispatch } from "react";
import { type NavigateFunction } from "react-router-dom";
import * as amplitude from "@amplitude/analytics-browser";
import i18n from '@app/dashboard/tools/i18n';
import { environment } from "@app/dashboard/environments/environment";
import { LOCAL_STORAGE_KEYS } from "@app/dashboard/constants/local-storage-keys";
import { COOKIES_KEYS } from "@app/dashboard/constants/cookies-keys";
import { PROFILE } from "@app/dashboard/constants/profile";
import { useIntegrationsStore } from "@app/dashboard/store/integrations";
import { useTestLiveModeStore } from "@app/dashboard/store/testLiveMode";
import { activeServices, checkBankAccount, loadPerms } from "@app/dashboard/services/outer_layout/login";
import { OUTER_ROUTES } from "@app/dashboard/routes/outer-routes";
import { addToLocalStorage, getFromLocalStorage } from "@app/dashboard/utils/manageLocalStorage";
import { getMerchantId, getMerchantProfileData, getProfileType, getUserId } from "@app/dashboard/utils/localStorageUtils";
import { saveTokenWithExpiry } from "@app/dashboard/tools/axios/axiosUtils";
import { getCookie } from "@app/dashboard/utils/manageCookies";
import { amplitudeSetProperty } from "@app/dashboard/tools/amplitude";

const { t } = i18n;

const {
  TEMP_TOKEN, COMPANY_NAME, COUNTRY, TOKEN, IS_BILLS_NEW, ID, MERCHANT_ID,
  NAME, FIRST_NAME, LAST_NAME, EMAIL, PHONE, FILLED_ANSWER, USER_ID,
  PAYOUT_ENABLED, PAYOUT_TERMS, PROFILE_TOKEN,
  FILLED_BUSINESS_DATA, PROFILE_TYPE, MERCHANT_ROLE,
  LOGO_URL, PERMISSIONS, NO_BANK,
  MERCHANT_STATEMENTS, DIGEST, INSTANT_SETTLEMENT_ENABLED,
  SHOW_DASHBOARD_TOUR, BILLS_ENABLED, IS_DISTRIBUTOR,
  AMPLITUDE_USER_ID_TRACKED, IS_PAYMOB_PRO_ENABLED, OPEN_PAYMOB_PRO_DRAWER, PAYMOB_PLAN,
  PAYMOB_PRO_END_DATE
} = LOCAL_STORAGE_KEYS;

export const redirectToChangePassword = (tempToken: string, navigate: NavigateFunction) => {
  const changePasswordRoute = OUTER_ROUTES.CHANGE_PASSWORD;

  addToLocalStorage(TEMP_TOKEN, tempToken);
  navigate(changePasswordRoute);
};

const constructUserPerms = (userPerms: any, permissionsDict: any) => {
  const userPermNames = [];

  for (let i = 0; i < userPerms.length; i++) {
    if (userPerms[i] in permissionsDict) {
      userPermNames.push(permissionsDict[userPerms[i]])
    }
  }
  return userPermNames;
};

const setDataToLocalStorage = (response: any) => {
  const accessToken = response.data.access;

  addToLocalStorage(COMPANY_NAME, response.data.profile.company_name ?
    response.data.profile.company_name
    : response.data.profile.user.username
  );

  addToLocalStorage(COUNTRY, response.data.profile.country);
  addToLocalStorage(INSTANT_SETTLEMENT_ENABLED, response.data.profile.instant_settlement_enabled);
  addToLocalStorage(TOKEN, accessToken);
  addToLocalStorage(IS_BILLS_NEW, response.data.profile.is_bills_new);
  addToLocalStorage(ID, response.data.profile.id);

  addToLocalStorage(MERCHANT_ID, response.data.profile.merchant_id ?
    response.data.profile.merchant_id
    : response.data.profile.id
  );

  addToLocalStorage(USER_ID, response.data.profile.user.id);
  addToLocalStorage(NAME, response.data.profile.user.username);
  addToLocalStorage(FIRST_NAME, response.data.profile.user.first_name);
  addToLocalStorage(LAST_NAME, response.data.profile.user.last_name);
  addToLocalStorage(EMAIL, response.data.profile.user.email);
  response.data.profile?.phones && addToLocalStorage(PHONE, response.data.profile.phones[0]);
  addToLocalStorage(FILLED_ANSWER, response.data.profile.filled_business_data);

  addToLocalStorage(PAYOUT_ENABLED, response.data?.profile?.payout_enabled);
  addToLocalStorage(PAYOUT_TERMS, response.data?.profile?.payout_terms);
  addToLocalStorage(PROFILE_TOKEN, response.data?.sso_token);

  addToLocalStorage(SHOW_DASHBOARD_TOUR, response.data?.profile.show_new_dashboard_tour);

  document.cookie =
    window.location.hostname === "acceptstagingtest.paymobsolutions.com" ?
      `IDMS_TOKEN=${response.data.sso_token}; path=/; Domain=.paymobsolutions.com`
      :
      `IDMS_TOKEN=${response.data.sso_token}; path=/; Domain=.paymob.com`;

  addToLocalStorage(FILLED_BUSINESS_DATA, response.data.profile.filled_business_data ? true : false);

  addToLocalStorage(PROFILE_TYPE, response.data.profile.profile_type);
  addToLocalStorage(MERCHANT_ROLE, response.data.profile.role_name);
  addToLocalStorage(LOGO_URL, response.data.profile.logo_url);
};

const setAmplitudeUserId = (userId: string) => {
  amplitude.setUserId(String(userId));
   addToLocalStorage(AMPLITUDE_USER_ID_TRACKED, true)
};

export const initAmplitudeUserId = () => {
  const isAmplitudeUserIdAdded = getFromLocalStorage(AMPLITUDE_USER_ID_TRACKED);

  if (!isAmplitudeUserIdAdded) {
    const userId = getUserId();
    setAmplitudeUserId(userId);
  }
};

const setAmplitudeData = (response: any) => {  
  const { phone, id: staffId } = getMerchantProfileData();
  const profileType = getProfileType();
  const merchantId = getMerchantId(); 
  const userId = getUserId();

  const isMerchant = profileType === PROFILE.TYPE.MERCHANT;
  const isStaff = profileType === PROFILE.TYPE.MERCHANT_STAFF;
  
  const userRole = isMerchant 
    ? 'primary_user' 
    : isStaff 
    ? 'sub_user' 
    : profileType || '';
    
  setAmplitudeUserId(userId);
  amplitudeSetProperty({
    merchant_country: response.data.profile.country,
    merchant_id: merchantId,
    user_role: userRole,
    phone_number: phone,
    user_id: userId,
   ...(isStaff && { staff_id: staffId })
  });
  amplitude.track('sign_in');
};

const checkActiveServices = async (token: string) => {

  await activeServices(token)
    .then((response) => {
      const services = response.data.results;

      services?.length &&
        services.forEach((service: any) => {

          const { service_name, active, service_id } = service;
          const serviceInfo = {
            enabled: !!service_name,
            id: service_id
          };

          service_name === environment.STATEMENTS_SERVICE_NAME && addToLocalStorage(MERCHANT_STATEMENTS, serviceInfo);

          service_name === environment.DIGEST_SERVICE_NAME && addToLocalStorage(DIGEST, serviceInfo);
        });
    })
    .catch((error) => {
      return Promise.reject(error);
    });
};

export const handleAfterLogin = async (response: any, navigate: NavigateFunction, setServerErrMsg?: Dispatch<string>) => {
  const accessToken = response.data.access;
  const refreshToken = response.data.refresh;
  const isBillsEnabled = response?.data?.profile?.is_bills_enable;
  const isDistributor = response?.data?.profile?.is_bills_distributor;
  const isPaymobProEnabled = response?.data?.paymob_pro;
  const paymobProPlan = response?.data?.paymob_pro_plan;
  const showPaymobProDrawer = response?.data?.show_paymob_pro_drawer;
  const paymobProEndDate = response?.data?.paymob_pro_end_date;

  addToLocalStorage(LOCAL_STORAGE_KEYS.TOKEN, accessToken);
  addToLocalStorage(LOCAL_STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
  addToLocalStorage(BILLS_ENABLED, isBillsEnabled);
  addToLocalStorage(IS_DISTRIBUTOR, isDistributor);
  addToLocalStorage(IS_PAYMOB_PRO_ENABLED, isPaymobProEnabled);
  addToLocalStorage(PAYMOB_PLAN, paymobProPlan);
  addToLocalStorage(OPEN_PAYMOB_PRO_DRAWER, showPaymobProDrawer);
  addToLocalStorage(PAYMOB_PRO_END_DATE, paymobProEndDate);

  saveTokenWithExpiry(accessToken);

  if (response.data.profile.is_temp_password) {
    redirectToChangePassword(accessToken, navigate);
  } else {

    const userObj = {
      id: response.data.profile.id,
      name: response.data.profile.user.username,
      permissions: response.data.profile.user.user_permissions,
      type: response.data.profile.profile_type
    }

    const { TYPE : { MERCHANT, MERCHANT_STAFF }} = PROFILE;

    if (
      (userObj.type === MERCHANT || userObj.type === MERCHANT_STAFF) &&
      !response.data.acq_partner
    ) {
      const permsResponse = await loadPerms(accessToken);
      if (permsResponse.status.toString().startsWith('2')) {
        const { getState: getIntegrationsStore } = useIntegrationsStore;
        const { getState: getTestLiveModeStore } = useTestLiveModeStore;
        const hasLiveIntegration = response.data.profile.has_live_integration;

        getIntegrationsStore().setHasLiveIntegration(hasLiveIntegration);

        if (hasLiveIntegration === true) {
          const modeFromCookies: boolean | null = JSON.parse(getCookie(COOKIES_KEYS.IS_LIVE_MODE) || 'null');

          typeof modeFromCookies === 'boolean' ?
            getTestLiveModeStore().setIsLive(modeFromCookies)
            :
            getTestLiveModeStore().setIsLive(true);
        } else {
          getTestLiveModeStore().setIsLive(false);
        }

        const finalPerms = constructUserPerms(userObj.permissions, permsResponse.data.data);
        addToLocalStorage(PERMISSIONS, finalPerms);

        setDataToLocalStorage(response);
        setAmplitudeData(response);

        const hasBankAccResponse = await checkBankAccount(accessToken);
        addToLocalStorage(NO_BANK, hasBankAccResponse.status.toString().startsWith('2') ? hasBankAccResponse.data.length === 0 : true);

        // await checkActiveServices(accessToken);

      } else {
        setServerErrMsg?.(t('errors.somethingWentWrong'));
      }

    } else {
      // Bank or partner user
      setServerErrMsg?.(`${t('outerLayout.form.login.errors.wrongCredentials')}..`);
    }
  }
};